import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Form, Input, Radio, Select } from '@arco-design/web-react';
import type { FormInstance } from '@arco-design/web-react/es/Form';

import type { CommonImageItemType } from '@/components-biz/ImageSelect';
import {
  MAAS_INPUT_PLACEHOLDER,
  MAAS_SELECT_PLACEHOLDER,
  maasRequiredRules,
} from '@/delivery-type-plugin/capabilities/maas-init/form-messages';
import { MAAS_INFERENCE_SOURCE_FIELD, MaasInferenceSource, MaasRunMode } from '@/delivery-type-plugin/capabilities/maas-init/maasTypes';
import {
  formJsonFieldFormatter,
  formJsonFieldNormalize,
  parseFormJsonField,
} from '@/delivery-type-plugin/capabilities/maas-init/utils/jsonFormField';
import type { ModelInstanceFormValues } from '@/delivery-type-plugin/capabilities/maas-model-storage/modelInstanceTypes';
import {
  INFERENCE_SYSTEM_FIELD,
  MASTER_SYSTEM_DISK_FIELD,
  WORKER_SYSTEM_DISK_FIELD,
} from '@/delivery-type-plugin/capabilities/maas-node-groups/nodeFieldNames';
import { resolveFrameworkFromInferenceImage } from '@/delivery-type-plugin/capabilities/maas-node-groups/payload';
import {
  buildImageItemFromInferenceSystem,
  hasPresetInferenceImage,
  parseInferenceFrameworkImageItem,
} from '@/delivery-type-plugin/capabilities/maas-system-config/presetInferenceImage';
import {
  ImagePullAuthFields,
  ImageResourceSelectField,
  resolveImageSummaryFromContainerForm,
  resolveImageSummaryFromItem,
} from '@/delivery-type-plugin/components';
import styles from '@/delivery-type-plugin/styles/maas-model-step.module.less';

import { useInferenceFrameworkSetting } from './useInferenceFrameworkSetting';

const FormItem = Form.Item;

interface InferenceFrameworkSettingProps {
  form: FormInstance;
  onImageSelected?: (item: CommonImageItemType) => void;
  compact?: boolean;
  isEdit?: boolean;
  initValues?: ModelInstanceFormValues;
  /** SKU 规格创建：按第一步所选架构过滤镜像 */
  presetArchitecture?: string;
  /** SKU presetConfig 已含镜像时锁定镜像选择器（下单场景） */
  presetImageLocked?: boolean;
  /** 下单：SKU 预置已有推理镜像时隐藏删/改 */
  lockActionsWhenFilled?: boolean;
  /** 仅展示自定义镜像地址，隐藏推理框架来源选择。 */
  customImageOnly?: boolean;
  /** 镜像后展示选填账密；训练集群传 false */
  showPullAuth?: boolean;
  /** 推理 system 字段前缀，默认 _inferenceSystem；embedded 抽屉为 system */
  systemField?: string;
  serviceField?: string;
  parentForm?: FormInstance;
  skipResetNodeGroups?: boolean;
  embeddedNodeConfig?: boolean;
}

/** 推理框架：写入 _inferenceSystem / _inferenceService，提交时再合并入主组 */
export function InferenceFrameworkSetting({
  form,
  onImageSelected,
  compact,
  isEdit,
  initValues,
  presetArchitecture,
  presetImageLocked,
  lockActionsWhenFilled,
  customImageOnly,
  showPullAuth = false,
  systemField: systemFieldProp,
  serviceField,
  parentForm,
  skipResetNodeGroups,
  embeddedNodeConfig,
}: InferenceFrameworkSettingProps) {
  const { t } = useTranslation();
  const runMode = Form.useWatch('runMode', parentForm ?? form) as MaasRunMode | undefined;
  const systemField = systemFieldProp ?? INFERENCE_SYSTEM_FIELD;
  const systemDiskField = runMode === MaasRunMode.PrimarySecondary ? MASTER_SYSTEM_DISK_FIELD : WORKER_SYSTEM_DISK_FIELD;
  const imageQueryObj = useMemo(() => (presetArchitecture ? { cpu: presetArchitecture } : {}), [presetArchitecture]);
  const imagePaths = useMemo(
    () => ({
      imageKey: `${systemField}.imageKey`,
      imageUrl: `${systemField}.imageName`,
      imagePrefix: systemField,
    }),
    [systemField],
  );
  const watchedImageKey = Form.useWatch(imagePaths.imageKey, form);
  const watchedImageUrl = Form.useWatch(imagePaths.imageUrl, form);
  const inferenceSystemRaw = Form.useWatch(systemField, form);
  const inferenceFrameworkImageRaw = Form.useWatch('inferenceFrameworkImage', form);

  const { imageMappings, inferenceSource, handleSourceChange, handleImageChange, handleImageClear, presetImageItem } =
    useInferenceFrameworkSetting({
      form,
      systemField,
      systemDiskField,
      isEdit,
      initValues,
      onImageSelected,
      presetArchitecture,
      presetImageLocked,
      serviceField,
      parentForm,
      skipResetNodeGroups,
      prefillAdvancedConfig: customImageOnly ? false : true,
    });

  useEffect(() => {
    if (customImageOnly) {
      form.setFieldValue(MAAS_INFERENCE_SOURCE_FIELD, MaasInferenceSource.Custom);
    }
  }, [customImageOnly, form]);

  const inferenceSourceOptions = useMemo(
    () => [
      { label: t('data-center:maas.inference.source.builtin'), value: MaasInferenceSource.Builtin },
      { label: t('data-center:maas.inference.source.custom'), value: MaasInferenceSource.Custom },
    ],
    [t],
  );

  const imageSummarySource = useMemo(() => {
    const flatImageEchoKey = [watchedImageKey, watchedImageUrl].join('|');
    void flatImageEchoKey;

    if (presetImageLocked && presetImageItem) {
      return resolveImageSummaryFromItem(presetImageItem);
    }
    const fromForm = resolveImageSummaryFromContainerForm(form.getFieldsValue() as Record<string, unknown>, imagePaths, presetArchitecture);
    if (fromForm) return fromForm;

    const system = parseFormJsonField<Record<string, unknown>>(inferenceSystemRaw);
    if (!system) return null;
    const catalogItem = parseInferenceFrameworkImageItem(inferenceFrameworkImageRaw) || buildImageItemFromInferenceSystem(system);
    if (!catalogItem) return null;

    return {
      ...resolveImageSummaryFromItem(catalogItem),
      cpuArchitectures: catalogItem.cpuArchitectures?.[0] || String(system.architecture ?? '').trim() || undefined,
      systemDiskSize: catalogItem.options?.systemImage?.systemVolumeSize ?? system.minSystemDiskGi,
    };
  }, [
    form,
    imagePaths,
    inferenceFrameworkImageRaw,
    inferenceSystemRaw,
    presetArchitecture,
    presetImageItem,
    presetImageLocked,
    watchedImageKey,
    watchedImageUrl,
  ]);

  /** 仅 SKU 预置已有镜像时锁定；用户下单自选不锁 */
  const presetHasImage = Boolean(initValues && hasPresetInferenceImage(initValues as Record<string, unknown>));
  /** 固定规格 / 预置锁定：整块只读；自定义下单仅在 SKU 预置有镜像时禁止改来源与删改 */
  const frameworkLocked = Boolean(presetImageLocked) || (Boolean(lockActionsWhenFilled) && presetHasImage);

  return (
    <>
      <FormItem field="framework" hidden>
        <Input />
      </FormItem>
      {!embeddedNodeConfig ? (
        <FormItem
          field={INFERENCE_SYSTEM_FIELD}
          hidden
          style={{ display: 'none', margin: 0 }}
          triggerPropName="value"
          formatter={formJsonFieldFormatter}
          normalize={formJsonFieldNormalize}
        >
          <Input />
        </FormItem>
      ) : null}
      {customImageOnly ? null : (
        // 须挂真实 field：编辑旧 preset 无 maasInferenceSource 时由 hook 回写，避免确认保存落到笼统「请填写必填项」
        <FormItem
          label={t('data-center:maas.inference.framework')}
          field={MAAS_INFERENCE_SOURCE_FIELD}
          required
          rules={frameworkLocked ? undefined : [{ required: true, message: t('common:required.field') }]}
          layout="horizontal"
          labelAlign="left"
          labelCol={{ flex: 'none' }}
          wrapperCol={{ flex: 'none' }}
          className={styles.deployModeRow}
          disabled={frameworkLocked}
        >
          {compact ? (
            <Radio.Group options={inferenceSourceOptions} type="button" size="small" onChange={handleSourceChange} />
          ) : (
            <Select options={inferenceSourceOptions} placeholder={MAAS_SELECT_PLACEHOLDER} onChange={handleSourceChange} />
          )}
        </FormItem>
      )}

      {!customImageOnly && inferenceSource === MaasInferenceSource.Builtin ? (
        <>
          <ImageResourceSelectField
            form={form}
            queryObj={imageQueryObj}
            variant="container"
            artifactType="inference_framework"
            lockArtifactType
            imageKeyField={imagePaths.imageKey}
            imageUrlField={imagePaths.imageUrl}
            imagePrefix={imagePaths.imagePrefix}
            readOnly={frameworkLocked}
            lockActionsWhenFilled={lockActionsWhenFilled}
            presetHasImage={presetHasImage}
            summarySource={imageSummarySource}
            onImageChange={handleImageChange}
            onImageClear={handleImageClear}
          />
          {imageMappings.map((mapping) => (
            <FormItem key={mapping.field} field={mapping.field} hidden>
              <Input />
            </FormItem>
          ))}
          <FormItem field="inferenceFrameworkImage" hidden>
            <Input />
          </FormItem>
        </>
      ) : (
        <FormItem
          label={t('data-center:maas.inference.custom.image')}
          field={`${systemField}.image`}
          rules={frameworkLocked ? undefined : maasRequiredRules(t('data-center:maas.inference.custom.image'))}
        >
          <Input
            placeholder={MAAS_INPUT_PLACEHOLDER}
            readOnly={frameworkLocked}
            disabled={frameworkLocked}
            onBlur={() => {
              if (frameworkLocked) return;
              const system = form.getFieldValue(systemField) as Record<string, unknown> | undefined;
              form.setFieldValue('framework', resolveFrameworkFromInferenceImage(null, system));
            }}
          />
        </FormItem>
      )}
      {showPullAuth ? <ImagePullAuthFields form={form} disabled={frameworkLocked} enableAuthSwitch={Boolean(embeddedNodeConfig)} /> : null}
    </>
  );
}

export default InferenceFrameworkSetting;
